import { useEffect, useState, useCallback } from "react";
import api, { fmtMXNFull, fmtDate } from "@/lib/api";
import PageHeader from "@/components/PageHeader";
import { HandCoins, Loader2, X, FileDown, Receipt, Search, Mail, MessageCircle, Banknote, Trash2, PenLine } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import SignaturePad from "@/components/SignaturePad";
import { toast } from "sonner";

const Stat = ({ label, value, color }) => (
  <div className="panel p-2.5">
    <div className="label-mono" style={{ color: "var(--text-mute)" }}>{label}</div>
    <div className="font-display font-bold text-base tabular-nums" style={{ color }}>{fmtMXNFull(value)}</div>
  </div>
);

export default function StaffContributions() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [q, setQ] = useState("");
  const [statement, setStatement] = useState(null);
  const [downloading, setDownloading] = useState(false);
  const [emailOpen, setEmailOpen] = useState(false);
  const [email, setEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [payFor, setPayFor] = useState(null); // nombre del colaborador a abonar
  const [payForm, setPayForm] = useState({ amount: "", payment_date: "", method: "efectivo", notes: "" });
  const [payingSave, setPayingSave] = useState(false);
  const [signFor, setSignFor] = useState(null); // abono a firmar
  const [signSaving, setSignSaving] = useState(false);

  const downloadPaymentPdf = async (p) => {
    try {
      const res = await api.get(`/staff-contributions/payments/${p.id}/pdf`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `abono_${(p.name || "").replace(/\s+/g, "_")}_${p.payment_date || ""}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Comprobante de abono descargado");
    } catch { toast.error("Error al generar el comprobante"); }
  };

  const saveSignature = async (dataUrl) => {
    setSignSaving(true);
    try {
      await api.post(`/staff-contributions/payments/${signFor.id}/signature`, { signature: dataUrl });
      toast.success("Firma guardada en el comprobante");
      setSignFor(null);
      if (statement?.summary?.name) openStatement(statement.summary.name);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al guardar la firma"); }
    finally { setSignSaving(false); }
  };

  const openPay = (name) => {
    setPayForm({ amount: "", payment_date: new Date().toISOString().slice(0, 10), method: "efectivo", notes: "" });
    setPayFor(name);
  };

  const savePayment = async (e) => {
    e.preventDefault();
    setPayingSave(true);
    try {
      const { data: created } = await api.post("/staff-contributions/payments", {
        name: payFor, amount: Number(payForm.amount), payment_date: payForm.payment_date,
        method: payForm.method, notes: payForm.notes,
      });
      toast.success(`Abono de ${fmtMXNFull(Number(payForm.amount))} registrado a ${payFor}`, {
        duration: 10000,
        action: { label: "Descargar comprobante", onClick: () => downloadPaymentPdf(created) },
      });
      setPayFor(null);
      load();
      if (statement?.summary?.name === payFor) openStatement(payFor);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al registrar el abono"); }
    finally { setPayingSave(false); }
  };

  const deletePayment = async (p) => {
    if (!window.confirm(`¿Eliminar el abono de ${fmtMXNFull(p.amount)} del ${fmtDate(p.payment_date)}?`)) return;
    try {
      await api.delete(`/staff-contributions/payments/${p.id}`);
      toast.success("Abono eliminado");
      load();
      openStatement(statement.summary.name);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al eliminar"); }
  };

  const sendByEmail = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await api.post("/staff-contributions/send-email", { name: statement.summary.name, email: email.trim() });
      toast.success(`Estado de cuenta enviado a ${email.trim()}`);
      setEmailOpen(false); setEmail("");
    } catch (err) { toast.error(err.response?.data?.detail || "No se pudo enviar el correo"); }
    finally { setSending(false); }
  };

  const shareWhatsApp = async () => {
    try {
      const { data: d } = await api.post("/staff-contributions/share", { name: statement.summary.name });
      let pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/public/staff-statement/${d.token}/pdf`;
      if (d.company_id && d.company_id !== "default") pdfUrl += `?c=${d.company_id}`;
      const s = d.summary;
      const msg = `Hola ${s.name}, te comparto tu estado de cuenta de aportaciones en obra:\n\n• Total aportado: $${s.aportado.toLocaleString("es-MX", { minimumFractionDigits: 2 })}\n• Reembolsado: $${s.reembolsado.toLocaleString("es-MX", { minimumFractionDigits: 2 })}\n• Pendiente por pagarte: $${s.pendiente.toLocaleString("es-MX", { minimumFractionDigits: 2 })}\n\nDescarga el PDF aquí (válido 30 días):\n${pdfUrl}`;
      window.open(`https://wa.me/?text=${encodeURIComponent(msg)}`, "_blank");
    } catch { toast.error("No se pudo generar el enlace"); }
  };

  const load = useCallback(async () => {
    const { data: d } = await api.get("/staff-contributions/accounts");
    setData(d);
  }, []);
  useEffect(() => { load(); }, [load]);

  const openStatement = async (name) => {
    try {
      const { data: d } = await api.get("/staff-contributions/statement", { params: { name } });
      setStatement(d);
    } catch { toast.error("Error al cargar el estado de cuenta"); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await api.get("/staff-contributions/statement/pdf", { params: { name: statement.summary.name }, responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `aportaciones_${statement.summary.name.replace(/\s+/g, "_")}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Estado de cuenta descargado");
    } catch { toast.error("Error al generar el PDF"); }
    finally { setDownloading(false); }
  };

  const accounts = (data?.accounts || []).filter((a) => a.name.toLowerCase().includes(q.toLowerCase()));

  return (
    <div className="p-4 lg:p-8 space-y-6" data-testid="staff-contributions-page">
      <PageHeader title="Aportaciones del Personal" subtitle="DINERO PUESTO EN OBRA POR RESIDENTES, SUPERVISORES Y AUXILIARES" />

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="w-6 h-6 animate-spin text-cyan-400" /></div>
      ) : (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="staff-totals">
            <Stat label="TOTAL APORTADO" value={data.totals.aportado} color="var(--text)" />
            <Stat label="REEMBOLSADO" value={data.totals.reembolsado} color="var(--green)" />
            <Stat label="PENDIENTE POR PAGAR" value={data.totals.pendiente} color={data.totals.pendiente > 0 ? "var(--red)" : "var(--green)"} />
            <div className="panel p-2.5">
              <div className="label-mono" style={{ color: "var(--text-mute)" }}>PERSONAS</div>
              <div className="font-display font-bold text-base" style={{ color: "var(--cyan)" }}>{data.totals.personas}</div>
            </div>
          </div>

          <div className="relative max-w-sm">
            <Search className="w-4 h-4 absolute left-2.5 top-2.5" style={{ color: "var(--text-mute)" }} />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar persona…" className="w-full input-cyber pl-8 pr-3 py-2 text-sm" data-testid="staff-search" />
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {accounts.length === 0 && <div className="col-span-full panel text-center py-12 text-slate-500">Sin aportaciones registradas. Se toman de los gastos marcados como pagados por PERSONA.</div>}
            {accounts.map((a) => (
              <div key={a.name} className="panel p-4 space-y-3" data-testid={`staff-card-${a.name.replace(/\s+/g, "-")}`}>
                <div className="flex items-center gap-2">
                  <div className="w-9 h-9 flex items-center justify-center rounded-sm" style={{ background: "rgba(255,42,141,0.08)", border: "1px solid rgba(255,42,141,0.3)" }}>
                    <HandCoins className="w-4 h-4" style={{ color: "var(--magenta)" }} />
                  </div>
                  <div className="min-w-0">
                    <div className="font-display font-bold text-slate-100 truncate">{a.name}</div>
                    <div className="text-[10px] text-slate-500 font-mono">{a.gastos} gastos · última {a.ultima_aportacion ? fmtDate(a.ultima_aportacion) : "—"}</div>
                  </div>
                  <div className="ml-auto flex items-center gap-1.5 shrink-0">
                    <button data-testid={`staff-pay-${a.name.replace(/\s+/g, "-")}`} onClick={() => openPay(a.name)}
                      className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1" style={{ color: "var(--green)", borderColor: "rgba(34,197,94,0.4)" }}>
                      <Banknote className="w-3.5 h-3.5" /> Abonar
                    </button>
                    <button data-testid={`staff-statement-${a.name.replace(/\s+/g, "-")}`} onClick={() => openStatement(a.name)}
                      className="btn-ghost px-2.5 py-1.5 rounded-sm text-[10px] uppercase tracking-widest font-semibold flex items-center gap-1" style={{ color: "var(--cyan)" }}>
                      <Receipt className="w-3.5 h-3.5" /> Cuenta
                    </button>
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  <Stat label="APORTADO" value={a.aportado} color="var(--text)" />
                  <Stat label="REEMBOLSADO" value={a.reembolsado} color="var(--green)" />
                  <Stat label="PENDIENTE" value={a.pendiente} color={a.pendiente > 0 ? "var(--red)" : "var(--green)"} />
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {payFor && (
        <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={() => setPayFor(null)}>
          <form onSubmit={savePayment} className="panel-strong w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="staff-pay-modal">
            <div className="panel-header px-6 py-4 flex items-center justify-between">
              <div>
                <div className="label-mono" style={{ color: "var(--green)" }}>// REGISTRAR ABONO</div>
                <h2 className="font-display font-bold text-xl text-slate-100">{payFor}</h2>
              </div>
              <button type="button" onClick={() => setPayFor(null)} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="label-mono block mb-1.5">Monto abonado *</label>
                  <input required type="number" step="0.01" min="0.01" value={payForm.amount} onChange={(e) => setPayForm({ ...payForm, amount: e.target.value })}
                    className="w-full input-cyber px-3 py-2 text-sm text-right" data-testid="staff-pay-amount" placeholder="20,000.00" />
                </div>
                <div>
                  <label className="label-mono block mb-1.5">Fecha del abono *</label>
                  <input required type="date" value={payForm.payment_date} onChange={(e) => setPayForm({ ...payForm, payment_date: e.target.value })}
                    className="w-full input-cyber px-3 py-2 text-sm" data-testid="staff-pay-date" />
                </div>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Método</label>
                <select value={payForm.method} onChange={(e) => setPayForm({ ...payForm, method: e.target.value })} className="w-full input-cyber px-3 py-2 text-sm" data-testid="staff-pay-method">
                  {["efectivo", "transferencia", "cheque", "otro"].map((m) => <option key={m} value={m} style={{ background: "#0a0f1d" }}>{m.toUpperCase()}</option>)}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Nota (opcional)</label>
                <input value={payForm.notes} onChange={(e) => setPayForm({ ...payForm, notes: e.target.value })}
                  className="w-full input-cyber px-3 py-2 text-sm" data-testid="staff-pay-notes" placeholder="Pago a cuenta de sus aportaciones" />
              </div>
              <div className="flex gap-2 justify-end pt-2">
                <button type="button" onClick={() => setPayFor(null)} className="btn-ghost px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm">Cancelar</button>
                <button type="submit" disabled={payingSave} data-testid="staff-pay-save" className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">
                  {payingSave ? "Guardando…" : "Registrar abono"}
                </button>
              </div>
            </div>
          </form>
        </div>
      )}

      {statement && (
        <div className="fixed inset-0 z-50 p-4 flex items-center justify-center" style={{ background: "rgba(5,8,18,0.85)", backdropFilter: "blur(6px)" }} onClick={() => setStatement(null)}>
          <div className="panel-strong w-full max-w-2xl overflow-y-auto scan-line" style={{ maxHeight: "calc(100dvh - 2rem)" }} onClick={(e) => e.stopPropagation()} data-testid="staff-statement-modal">
            <div className="panel-header px-6 py-4 flex items-center justify-between sticky top-0 z-10">
              <div>
                <div className="label-mono">// ESTADO DE CUENTA</div>
                <h2 className="font-display font-bold text-xl text-slate-100">{statement.summary.name}</h2>
              </div>
              <div className="flex items-center gap-2">
                <button onClick={() => { setEmailOpen(true); setEmail(""); }} data-testid="staff-email-btn"
                  className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5"
                  style={{ color: "var(--cyan)", borderColor: "rgba(34,211,238,0.4)" }}>
                  <Mail className="w-3.5 h-3.5" /> Correo
                </button>
                <button onClick={shareWhatsApp} data-testid="staff-whatsapp-btn"
                  className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5"
                  style={{ color: "#25D366", borderColor: "rgba(37,211,102,0.4)" }}>
                  <MessageCircle className="w-3.5 h-3.5" /> WhatsApp
                </button>
                <button onClick={downloadPdf} disabled={downloading} data-testid="staff-pdf-btn"
                  className="btn-ghost px-3 py-1.5 rounded-sm text-[11px] uppercase tracking-widest font-semibold flex items-center gap-1.5 disabled:opacity-50"
                  style={{ color: "var(--green)", borderColor: "rgba(34,197,94,0.4)" }}>
                  {downloading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <FileDown className="w-3.5 h-3.5" />} PDF
                </button>
                <button onClick={() => setStatement(null)} className="text-slate-500 hover:text-cyan-300"><X className="w-5 h-5" /></button>
              </div>
            </div>
            <div className="p-6 space-y-4">
              {emailOpen && (
                <form onSubmit={sendByEmail} className="panel p-3 flex items-end gap-2" data-testid="staff-email-form">
                  <div className="flex-1">
                    <label className="label-mono block mb-1.5">Correo del colaborador</label>
                    <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)}
                      className="w-full input-cyber px-3 py-2 text-sm" data-testid="staff-email-input" placeholder="colaborador@correo.com" />
                  </div>
                  <button type="submit" disabled={sending} data-testid="staff-email-send"
                    className="btn-primary px-4 py-2 text-xs uppercase tracking-widest font-semibold rounded-sm disabled:opacity-50">
                    {sending ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enviar"}
                  </button>
                  <button type="button" onClick={() => setEmailOpen(false)} className="btn-ghost px-3 py-2 text-xs rounded-sm">✕</button>
                </form>
              )}
              <div className="grid grid-cols-3 gap-2">
                <Stat label="APORTADO" value={statement.summary.aportado} color="var(--text)" />
                <Stat label="REEMBOLSADO" value={statement.summary.reembolsado} color="var(--green)" />
                <Stat label="PENDIENTE" value={statement.summary.pendiente} color={statement.summary.pendiente > 0 ? "var(--red)" : "var(--green)"} />
              </div>
              {(statement.payments || []).length > 0 && (
                <div className="space-y-1.5" data-testid="staff-payments-list">
                  <div className="flex items-center justify-between">
                    <div className="label-mono" style={{ color: "var(--green)" }}>// ABONOS RECIBIDOS ({statement.payments.length})</div>
                    <span className="text-[10px] font-mono" style={{ color: "var(--green)" }}>Total abonado: {fmtMXNFull(statement.summary.abonado || 0)}</span>
                  </div>
                  {statement.payments.map((p) => (
                    <div key={p.id} className="panel p-2.5 flex items-center gap-3" style={{ borderColor: "rgba(34,197,94,0.25)" }}>
                      <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest shrink-0" style={{ color: "var(--green)", border: "1px solid var(--green)" }}>ABONO</span>
                      <div className="flex-1 min-w-0">
                        <div className="text-xs text-slate-200 truncate">{p.notes || "Pago a cuenta"}</div>
                        <div className="text-[10px] text-slate-500 font-mono">{fmtDate(p.payment_date)} · {(p.method || "").toUpperCase()}{p.created_by_name ? ` · registró ${p.created_by_name}` : ""}</div>
                      </div>
                      <span className="font-mono text-sm shrink-0" style={{ color: "var(--green)" }}>{fmtMXNFull(p.amount)}</span>
                      {p.signature ? (
                        <span className="text-[8px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest shrink-0" style={{ color: "var(--cyan)", border: "1px solid var(--cyan)" }} data-testid={`staff-payment-signed-${p.id}`}>FIRMADO</span>
                      ) : (
                        <button data-testid={`staff-payment-sign-${p.id}`} onClick={() => setSignFor(p)} title="Firmar de recibido"
                          className="text-slate-500 hover:text-cyan-300 shrink-0"><PenLine className="w-3.5 h-3.5" /></button>
                      )}
                      <button data-testid={`staff-payment-pdf-${p.id}`} onClick={() => downloadPaymentPdf(p)} title="Comprobante PDF"
                        className="text-slate-500 hover:text-green-400 shrink-0"><FileDown className="w-3.5 h-3.5" /></button>
                      {user?.role === "admin" && (
                        <button data-testid={`staff-payment-delete-${p.id}`} onClick={() => deletePayment(p)} className="text-slate-600 hover:text-red-400 shrink-0"><Trash2 className="w-3.5 h-3.5" /></button>
                      )}
                    </div>
                  ))}
                </div>
              )}
              <div className="label-mono" style={{ color: "var(--text-mute)" }}>// GASTOS APORTADOS</div>
              <div className="space-y-1.5" data-testid="staff-timeline">
                {statement.movements.map((m, i) => (
                  <div key={i} className="panel p-2.5 flex items-center gap-3">
                    <span className="text-[9px] font-bold px-1.5 py-0.5 rounded-sm tracking-widest shrink-0"
                      style={m.reimbursed ? { color: "var(--green)", border: "1px solid var(--green)" } : { color: "#fbbf24", border: "1px solid #fbbf24" }}>
                      {m.reimbursed ? "PAGADO" : (m.status || "").toUpperCase()}
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="text-xs text-slate-200 truncate">{m.description}</div>
                      <div className="text-[10px] text-slate-500 font-mono truncate">
                        {fmtDate(m.date)} · {m.project}
                        {m.reimbursed && m.paid_at ? ` · reembolsado ${fmtDate(m.paid_at)}${m.paid_by_name ? ` por ${m.paid_by_name}` : ""}` : ""}
                      </div>
                    </div>
                    <span className="font-mono text-sm text-slate-100 shrink-0">{fmtMXNFull(m.amount)}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {signFor && (
        <SignaturePad
          title={`${signFor.name} · Abono de ${fmtMXNFull(signFor.amount)} del ${fmtDate(signFor.payment_date)}`}
          saving={signSaving}
          onConfirm={saveSignature}
          onClose={() => setSignFor(null)}
        />
      )}
    </div>
  );
}
